import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, KeyRound, ExternalLink } from "lucide-react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SdkTest from "@/components/SdkTest";

export default function Settings() {
  const [sdkKey, setSdkKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSdkKey(r.data.matterport_sdk_key || "")).catch(() => toast.error("Could not load settings")).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { matterport_sdk_key: sdkKey.trim() });
      toast.success("Settings saved");
    } catch {
      toast.error("Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="h-16 border-b border-white/10 flex items-center px-8 md:sticky md:top-0 bg-background/90 backdrop-blur z-10">
        <h1 className="font-display text-xl font-bold tracking-tight">Settings</h1>
      </div>

      <div className="p-8 max-w-2xl">
        <div className="border border-white/10 bg-[#121212] p-8">
          <div className="flex items-center gap-2 mb-1">
            <KeyRound className="w-4 h-4 text-[#C5A24A]" />
            <h2 className="font-display font-bold tracking-tight">Matterport SDK</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-6">
            Enter your Matterport <span className="text-white">SDK Application Key</span> once here — it applies to every
            project. Each property still uses its own Model ID for the scan.
          </p>

          {loading ? (
            <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label className="label-caps text-muted-foreground">SDK Application Key</Label>
                <Input
                  data-testid="global-sdkkey-input"
                  value={sdkKey}
                  onChange={(e) => setSdkKey(e.target.value)}
                  className="mt-1.5 bg-[#0A0A0A] border-white/10 font-mono text-sm h-11"
                  placeholder="e.g. a1b2c3d4e5f6g7h8"
                />
                <a
                  href="https://support.matterport.com/s/article/Getting-Started-with-the-Matterport-SDK"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-[#D8BE86] hover:underline mt-2"
                >
                  Where do I find this? <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <Button data-testid="global-settings-save" onClick={save} disabled={saving} className="bg-[#C5A24A] hover:bg-[#A8863A] text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
              </Button>
            </div>
          )}
        </div>

        {!loading && <SdkTest sdkKey={sdkKey} />}

        <p className="text-xs text-muted-foreground mt-4">
          Your Matterport account → <span className="text-white">Settings → Developer Tools → SDK Key</span> generates an
          Application Key. The free Pro account supports this.
        </p>
      </div>
    </Layout>
  );
}
